import React, { useState } from "react";
import { Badge, Button, Card } from "react-bootstrap";
import { FaEye, FaLock, FaFolderOpen } from "react-icons/fa";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { useQueryClient } from "@tanstack/react-query";

import { useAuth } from "../../context/AuthContext";
import { useCloseProjet } from "../../hooks/PROJET/useCloseProjets";
import { useReopenProjet } from "../../hooks/PROJET/useReopenProjet";
import { formatDateShort } from "../../utils/date";
import ConfirmModal from "../../components/Modals/ConfirmModal";

const BTN_STYLE = {
  width: "32px", height: "32px", padding: 0,
  display: "flex", alignItems: "center", justifyContent: "center",
};

const STATUT_BADGE = {
  open:        { bg: "primary",   label: "Ouvert" },
  in_progress: { bg: "warning",   label: "En cours" },
  closed:      { bg: "secondary", label: "Clôturé" },
  approved:    { bg: "success",   label: "Approuvé" },
};

const ProjetRow = ({ projet, listState, asCard = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { hasPermission } = useAuth();
  const queryClient = useQueryClient();

  const closeProjet  = useCloseProjet();
  const reopenProjet = useReopenProjet();

  const [closing,          setClosing]          = useState(false);
  const [reopening,        setReopening]        = useState(false);
  const [showConfirmClose, setShowConfirmClose] = useState(false);

  const badge    = STATUT_BADGE[projet.status] ?? { bg: "light", label: projet.status };
  const isClosed = projet.status === "closed" || projet.status === "approved";
  const canClose  = hasPermission("CLOSE_PROJECT");
  const canReopen = hasPermission("OPEN_PROJECT");

  const handleView = () => {
    navigate(`/projet/${projet.id}`, {
      state: {
        from:      location.pathname,
        listState: listState,
      },
    });
  };

  const handleClose = (e) => {
    e.stopPropagation();
    setShowConfirmClose(true);
  };

  const doClose = async () => {
    setShowConfirmClose(false);
    setClosing(true);
    try {
      await closeProjet.mutateAsync(projet.id);
      queryClient.invalidateQueries({ queryKey: ["projets"] });
      toast.success("Projet clôturé");
    } catch (err) {
      toast.error(`Erreur : ${err.message}`);
    } finally {
      setClosing(false);
    }
  };

  const handleReopen = async (e) => {
    e.stopPropagation();
    setReopening(true);
    try {
      await reopenProjet.mutateAsync(projet.id);
      queryClient.invalidateQueries({ queryKey: ["projets"] });
      toast.success("Projet réouvert");
    } catch (err) {
      toast.error(`Erreur : ${err.message}`);
    } finally {
      setReopening(false);
    }
  };

  const actionButtons = (
    <div className="d-flex gap-1 flex-wrap">
      <Button variant="secondary" size="sm" style={BTN_STYLE} onClick={handleView} title="Voir le projet">
        <FaEye />
      </Button>
      {canClose && !isClosed && (
        <Button variant="warning" size="sm" style={BTN_STYLE} onClick={handleClose} disabled={closing} title="Clôturer">
          <FaLock style={{ color: "white" }} />
        </Button>
      )}
      {canReopen && isClosed && (
        <Button variant="success" size="sm" style={BTN_STYLE} onClick={handleReopen} disabled={reopening} title="Rouvrir">
          <FaFolderOpen style={{ color: "white" }} />
        </Button>
      )}
    </div>
  );

  const confirmCloseModal = (
    <ConfirmModal
      show={showConfirmClose}
      onHide={() => setShowConfirmClose(false)}
      onConfirm={doClose}
      title="Clôturer le projet"
      message={`Êtes-vous sûr de vouloir clôturer le projet « ${projet.title} » ?`}
      confirmLabel="Clôturer"
      confirmVariant="secondary"
    />
  );

  if (asCard) {
    return (
      <Card className="shadow-sm h-100">
        <Card.Body>
          <div className="d-flex justify-content-between align-items-start mb-2">
            <div>
              <div className="fw-semibold">{projet.title}</div>
              <small className="text-muted">#{projet.id}</small>
            </div>
            <Badge bg={badge.bg}>{badge.label}</Badge>
          </div>

          <div className="text-muted small mb-1">
            Client : {projet.client?.name ?? "—"}
          </div>
          <div className="text-muted small mb-1">
            Début : {projet.start_date ? formatDateShort(projet.start_date) : "—"}
          </div>
          <div className="text-muted small mb-3">
            Fin prévue : {projet.end_date ? formatDateShort(projet.end_date) : "—"}
          </div>

          {actionButtons}
          {confirmCloseModal}
        </Card.Body>
      </Card>
    );
  }

  return (
    <tr>
      <td>{projet.id}</td>
      <td>{projet.title}</td>
      <td>{projet.client?.name ?? "—"}</td>
      <td><Badge bg={badge.bg}>{badge.label}</Badge></td>
      <td>{projet.start_date ? formatDateShort(projet.start_date) : "—"}</td>
      <td>{projet.end_date   ? formatDateShort(projet.end_date)   : "—"}</td>
      <td>{actionButtons}{confirmCloseModal}</td>
    </tr>
  );
};

export default ProjetRow;
